/** Copyright 2026 Midas Healthcare Solutions - All Rights Reserved **/
import { Injectable } from '@angular/core';
import { ApiService } from '@midas/shared/common';
import { catchError, finalize, map, Observable, of, shareReplay } from 'rxjs';

type CachedProfilePicture = {
  version: number;
  blob: Blob;
  url: string;
};

@Injectable({ providedIn: 'root' })
export class ProfilePictureCacheService {
  private readonly cache = new Map<string, CachedProfilePicture>();
  private readonly inFlight = new Map<string, Observable<string | null>>();

  constructor(private readonly api: ApiService) {}

  getProfilePicture(userId: string, version: number | null): Observable<string | null> {
    if (version == null) {
      this.clearUser(userId);
      return of(null);
    }

    const cached = this.cache.get(userId);
    if (cached && cached.version === version) {
      return of(cached.url);
    }

    const requestKey = `${userId}::${version}`;
    const existingRequest = this.inFlight.get(requestKey);
    if (existingRequest) return existingRequest;

    const request$ = this.api.getBlob('/user/profile-picture').pipe(
      map((blob: Blob) => {
        const newUrl = URL.createObjectURL(blob);
        if (cached?.url) {
          URL.revokeObjectURL(cached.url);
        }
        this.cache.set(userId, {
          version,
          blob,
          url: newUrl,
        });

        return newUrl;
      }),
      catchError(() => {
        this.clearUser(userId);
        return of(null);
      }),
      finalize(() => {
        this.inFlight.delete(requestKey);
      }),
      shareReplay(1),
    );

    this.inFlight.set(requestKey, request$);
    return request$;
  }

  private clearUser(userId: string): void {
    const cached = this.cache.get(userId);
    if (cached?.url) {
      URL.revokeObjectURL(cached.url);
    }

    this.cache.delete(userId);
  }
}
